import { useEffect, useRef, type MutableRefObject } from 'react'

/**
 * Returns a ref that tracks the latest value of `value` and commits the
 * write in a post-commit effect (not during render).  Use when you need
 * a stable identity for consumers (effect dep arrays, event handlers that
 * outlive a single render) but need to read the latest value at call time.
 *
 * The returned ref object is stable for the life of the hook instance —
 * its identity never changes, so it is safe to list in effect dep arrays
 * without re-triggering the effect on every render.
 *
 * Why commit-phase (not render-phase):
 * Writing `ref.current = value` during render is fragile under Suspense —
 * React may throw a partial render away, but the ref write has already
 * happened.  Readers in later commits can then observe a stale value.
 * Assigning inside `useEffect` commits the write only when the render
 * is committed.
 *
 * The returned ref is typed as mutable (not readonly) because many
 * callers intentionally overwrite `.current` from event handlers to
 * eagerly clear or set a value before the next render (e.g. a pending
 * unfollow toast that must not re-fire on unmount).  Such handler-level
 * writes happen outside render and are safe.
 */
export default function useLatest<T> (value: T): MutableRefObject<T> {
  const ref = useRef(value)
  useEffect(() => {
    ref.current = value
  }, [value])
  return ref
}
